import { useCallback, useMemo } from 'react';
import { useOpenModal } from '@rainbow-modules/hooks';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import { Channel } from 'data/firestore/globals';
import useMultipartMutation from 'data/hooks/useMultipartMutation';
import { FEEBACKHUB_BASE_URL, REPORT_FEEDBACK_MODAL } from '../constants';

export type FeedbackType = 'conversation' | 'message' | 'note' | 'opportunity-summary' | 'call';

export type FeedbackContext =
    | { type: 'conversation'; customerId: string; threadId: string; channel: Channel; }
    | { type: 'message'; customerId: string; threadId: string; channel: Channel; messageId: string }
    | { type: 'note'; customerId: string; noteId: string }
    | { type: 'opportunity-summary'; opportunityId: string; summaryId: string; summary?: string }
    | { type: 'call'; sessionId: string };

interface SendFeedbackParams {
    agentId: string;
    context: FeedbackContext;
    title?: string;
}

interface SendFeedbackData {
    text: string;
    attachedFiles?: File[];
}

const buildFormData = (values: Record<string, unknown>) => {
    const data = new FormData();
    Object.keys(values).forEach(
        (key) => {
            const value = values[key];
            if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
                value.forEach((file) => data.append(key, file));
            } else {
                data.append(key, value as any);
            }
        },
    );
    return data;
};

const useSendFeedback = () => {
    const [openModal, closeModal] = useOpenModal(REPORT_FEEDBACK_MODAL);

    const {
        mutateAsync: createFeedback,
    } = useMultipartMutation({
        server: FEEBACKHUB_BASE_URL,
        pathname: '/api/v1/feedback',
        method: 'POST',
    });

    const sendFeedback = useCallback(
        (params: SendFeedbackParams) => {
            openModal({
                title: params.title || 'Report Issue',
                agentId: params.agentId,
                context: params.context,
                onSubmit: async ({ text, attachedFiles }: SendFeedbackData) => {
                    showAppSpinner();
                    try {
                        await createFeedback({
                            body: buildFormData({
                                agentId: params.agentId,
                                text,
                                attachedFiles,
                                ...params.context,
                            }),
                        });
                        closeModal();
                        showAppNotification({
                            title: 'Success',
                            description: 'Feedback sent successfully',
                            icon: 'success',
                            timeout: 5000,
                        });
                    } catch (error) {
                        showAppNotification({
                            title: 'Error',
                            description: 'Failed to send feedback',
                            icon: 'error',
                            timeout: 5000,
                        });
                    }
                    hideAppSpinner();
                },
            });
        },
        [openModal, createFeedback, closeModal],
    );

    return useMemo(
        () => ({
            sendFeedback,
            closeFeedback: closeModal,
        }),
        [sendFeedback, closeModal],
    );
};

export default useSendFeedback;
